/* ============================================================
   Fence Works — Import & export: the pure half
   Everything the Import & export screen (dataio.tsx) decides lives here as
   plain functions, so scripts/unit.mjs can test it in a bare vm: which
   column is which, what a row turns into, whether it is somebody we
   already have, what an import adds and changes, and how to take it back.

   The rules, in one place:
     • nothing is overwritten. A customer we already have only gets the
       fields that are BLANK on their record filled in (upsertCustomerInList's
       rule), and a product is only updated when the owner ticks "update
       products that already exist".
     • every record an import creates carries imported: { batchId, source,
       at }. Every change it makes to an existing record is written on that
       record (importFills / importPrev) with the value it set, so Undo can
       take back exactly that — and leaves alone anything edited since.
     • imported records are stamped createdAt = the moment of the import, not
       the date in the file: the sync's rescue merge (app.tsx) keeps records
       "created in this tab since it loaded", and a 2019 createdAt would make
       a freshly imported customer look old enough to drop. The file's date
       rides along as customerSince / scheduledStart / completedAt.
   ============================================================ */
import { normHeader, parseMoney, parseDateLoose } from "./csv";
import { newId } from "./ids";
import { docState, paidOf, DOC_LABEL } from "./quotestate";

const IMPORT_MAX_BYTES = 5 * 1024 * 1024;
const IMPORT_MAX_ROWS = 10000;
const PREVIEW_ROWS = 20;
const HISTORY_KEEP = 20;
/* The cloud save prunes deleted rows with one `id in (…)` filter per table,
   which rides the request URL. A few hundred ids is past what the gateway
   takes, so an undo in the cloud removes records a slice at a time and waits
   for each save (see undoImport in dataio.tsx). Products carry a longer,
   workspace-prefixed row id, so their slice is smaller. The same URL is why
   an import can't take the price book past PRICEBOOK_MAX: the first save
   after a reload prunes every product's legacy id in one filter. */
const UNDO_SLICE = 100;
const UNDO_SLICE_PRODUCTS = 40;
const PRICEBOOK_MAX = 250;

/* ---- the fields each import fills, and what other apps call them ----
   `syn` is matched with normHeader (case, spaces and punctuation ignored),
   in order: the first synonym found in the file wins. Service / property
   addresses come before billing ones — the fence goes where the work is. */
const CUSTOMER_FIELDS = [
  { key: "name", label: "Name", hint: "Full name — or map first and last name below",
    syn: ["name", "full name", "display name", "customer", "customer name", "customer full name", "customer display name", "client", "client name", "client full name", "contact name", "contact", "contact full name", "account name"] },
  { key: "firstName", label: "First name", syn: ["first name", "first", "firstname", "given name", "fname", "customer first name", "client first name", "contact first name", "billing first name"] },
  { key: "lastName", label: "Last name", syn: ["last name", "last", "lastname", "surname", "family name", "lname", "customer last name", "client last name", "contact last name", "billing last name"] },
  { key: "company", label: "Company", hint: "Used as the name when a row has no person's name",
    syn: ["company", "company name", "business", "business name", "organization", "organization name", "organization 1 - name"] },
  { key: "email", label: "Email",
    syn: ["email", "e-mail", "email address", "e-mail address", "emails", "e-mails", "main email", "primary email", "email 1", "e-mail 1 - value", "email 1 - value", "customer email", "client email", "contact email", "billing email"] },
  // a mobile number first: it's the one that can take a text
  { key: "phone", label: "Phone",
    syn: ["mobile", "mobile phone", "mobile phone #s", "mobile number", "mobile phone number", "cell", "cell phone", "cell number", "phone", "phone number", "main phone", "main phone #s", "primary phone", "phone 1 - value", "phones", "phone numbers", "telephone", "tel", "customer phone", "client phone", "contact phone", "billing phone", "home phone", "home number", "work phone", "work number"] },
  { key: "phone2", label: "Other phone", hint: "Goes into the customer's notes",
    syn: ["other phone", "other phone #s", "main phone", "main phone #s", "home phone", "home phone #s", "home number", "work phone", "work phone #s", "work number", "phone 2", "phone 2 - value", "alt phone", "alternate phone", "secondary phone", "business phone", "phone", "phone number"] },
  { key: "street", label: "Street",
    syn: ["street", "street 1", "street address", "address", "address 1", "address line 1", "service street", "service street 1", "service address line 1", "property street", "property street 1", "property address", "job street", "billing street", "billing street 1", "billing address line 1", "billing address 1", "bill street", "mailing street", "address 1 - street"] },
  { key: "street2", label: "Street line 2",
    syn: ["street 2", "address 2", "address line 2", "street line 2", "service street 2", "property street 2", "billing street 2", "billing address line 2", "unit", "apt", "suite"] },
  { key: "city", label: "City", syn: ["city", "town", "service city", "property city", "billing city", "bill city", "mailing city", "address 1 - city"] },
  { key: "state", label: "State", syn: ["state", "province", "state/province", "region", "service state", "service province", "property state", "billing state", "billing province", "bill state", "mailing state", "address 1 - region", "st"] },
  { key: "zip", label: "ZIP", syn: ["zip", "zip code", "zipcode", "postal code", "zip/postal", "zip/postal code", "postcode", "postal", "service zip code", "service zip", "service postal code", "property zip", "billing zip", "billing zip code", "billing postal code", "bill zip", "mailing zip", "address 1 - postal code"] },
  { key: "address", label: "Full address", hint: "One column with the whole address — only when there's no street / city / state / ZIP",
    syn: ["full address", "service address", "billing address", "mailing address", "site address", "job address", "location", "address 1 - formatted"] },
  { key: "tags", label: "Tags", syn: ["tags", "tag", "labels", "customer tags", "client tags", "groups", "group membership", "customer type", "client type"] },
  { key: "source", label: "Lead source", syn: ["lead source", "source", "leadsource", "referral source", "lead source name", "how did you hear about us", "heard about us", "marketing source", "channel", "referred by"] },
  { key: "notes", label: "Notes", syn: ["notes", "note", "customer notes", "client notes", "comments", "internal notes", "memo", "description"] },
  { key: "created", label: "Customer since", syn: ["customer since", "client since", "created", "created date", "created at", "date created", "created on", "date added", "added", "signup date", "since", "first job date"] },
];

const JOB_FIELDS = [
  { key: "name", label: "Customer", hint: "Links the job to that customer — a new customer is added if there isn't one",
    syn: ["customer", "customer name", "client", "client name", "name", "display name", "customer display name", "contact name", "full name", "bill to", "account name"] },
  { key: "firstName", label: "First name", syn: ["first name", "firstname", "customer first name", "client first name"] },
  { key: "lastName", label: "Last name", syn: ["last name", "lastname", "customer last name", "client last name"] },
  { key: "email", label: "Email", syn: ["email", "e-mail", "email address", "customer email", "client email", "emails", "e-mails"] },
  { key: "phone", label: "Phone", syn: ["phone", "phone number", "mobile", "mobile phone", "mobile number", "main phone", "cell", "customer phone", "client phone"] },
  { key: "street", label: "Street", syn: ["street", "street 1", "street address", "address", "address 1", "address line 1", "job street", "service street", "service street 1", "property street", "property address", "site street", "billing street", "billing street 1", "billing address line 1"] },
  { key: "city", label: "City", syn: ["city", "job city", "service city", "property city", "billing city"] },
  { key: "state", label: "State", syn: ["state", "province", "job state", "service state", "service province", "property state", "billing state"] },
  { key: "zip", label: "ZIP", syn: ["zip", "zip code", "postal code", "zip/postal", "postcode", "job zip", "service zip code", "service zip", "property zip", "billing zip", "billing zip code"] },
  { key: "address", label: "Full address", hint: "Only when there's no street / city / state / ZIP",
    syn: ["job address", "service address", "property address", "site address", "full address", "location", "billing address"] },
  { key: "title", label: "Job", syn: ["job title", "title", "job name", "job", "job description", "description", "service", "services", "work description", "scope", "fence type", "product", "style", "line items"] },
  { key: "jobNumber", label: "Job number", hint: "Their number in your old system — also stops a second import of the same file doubling up",
    syn: ["job #", "job number", "job no", "job id", "j-id", "jid", "invoice #", "invoice number", "invoice no", "work order #", "work order number", "wo #", "reference", "ref #"] },
  { key: "status", label: "Status", syn: ["status", "job status", "stage", "job stage", "work status"] },
  { key: "total", label: "Contract value", syn: ["total", "job total", "contract value", "contract amount", "contract total", "amount", "price", "total price", "invoice total", "invoice amount", "revenue", "sale amount", "sale price", "job value", "grand total", "total amount", "value", "subtotal"] },
  { key: "start", label: "Install / start date", syn: ["install date", "start date", "scheduled date", "scheduled start", "scheduled start date", "start", "visit date", "appointment date", "scheduled for", "date scheduled", "job date", "date", "scheduled"] },
  { key: "end", label: "End date", syn: ["end date", "scheduled end", "scheduled end date", "end"] },
  { key: "completed", label: "Completed date", syn: ["completed date", "completed", "completed at", "completed on", "date completed", "completion date", "closed date", "closed at", "date closed", "finished", "finish date", "finished date", "done date"] },
  { key: "lf", label: "Linear feet", syn: ["linear feet", "lf", "footage", "feet", "total feet", "length", "length ft", "linear ft"] },
  { key: "laborCost", label: "Labor cost", money: true, syn: ["labor cost", "labour cost", "labor", "labor total", "cost of labor", "labor expense", "labor costs"] },
  { key: "materialCost", label: "Material cost", money: true, syn: ["material cost", "materials cost", "materials", "material", "material total", "cost of materials", "material expense", "materials costs", "parts cost"] },
  { key: "hours", label: "Hours", syn: ["hours", "labor hours", "man hours", "crew hours", "total hours", "hours worked", "time spent"] },
  { key: "notes", label: "Notes", syn: ["notes", "job notes", "instructions", "details", "internal notes", "comments", "description"] },
];

const PRODUCT_FIELDS = [
  { key: "name", label: "Product name", syn: ["name", "product name", "product", "product/service", "product/service name", "item", "item name", "product or service", "service name", "description", "item description", "style name"] },
  { key: "category", label: "Material", hint: "Wood, vinyl, chain link, aluminum… — worked out from the name when blank",
    syn: ["category", "material", "fence type", "type", "product type", "material type", "fence material", "product category"] },
  { key: "style", label: "Style", syn: ["style", "fence style", "profile", "design", "sub", "subcategory"] },
  { key: "color", label: "Color", syn: ["color", "colour", "finish"] },
  { key: "height", label: "Height", syn: ["height", "height ft", "height (ft)", "fence height", "ht"] },
  { key: "matPerFt", label: "Material cost / ft", money: true, hint: "What you pay per foot, posts and hardware included",
    syn: ["material cost per ft", "material cost / ft", "material cost per foot", "material per ft", "cost per ft", "cost per foot", "cost/ft", "material cost", "cost", "unit cost", "purchase cost", "net price", "your price", "dealer price", "wholesale", "wholesale price", "supplier price", "our cost"] },
  { key: "matPerSection", label: "Material cost / section", money: true, hint: "What one panel or section costs you",
    syn: ["material cost per section", "material cost / section", "cost per section", "cost per panel", "section cost", "panel cost", "section price", "panel price", "price per section", "price per panel"] },
  { key: "sectionLen", label: "Section length (ft)", syn: ["section length", "section length ft", "panel length", "panel width", "section width", "panel length ft", "section ft"] },
  { key: "postCost", label: "Post cost", money: true, syn: ["post cost", "post price", "post kit", "post", "posts"] },
  { key: "gateCost", label: "Gate cost", money: true, hint: "Material cost of a walk gate",
    syn: ["gate cost", "gate price", "walk gate", "walk gate cost", "walk gate price", "gate", "gate material"] },
  { key: "sellPerFt", label: "Customer price / ft", money: true, hint: "What you charge per foot — used to work out a cost when the file has none",
    syn: ["customer price per ft", "customer price / ft", "price per ft", "price per foot", "price/ft", "sell price", "sale price", "sales price", "sales price / rate", "sales price/rate", "rate", "unit price", "price", "retail", "retail price", "installed price"] },
  { key: "laborPerFt", label: "Labor / ft", money: true, hint: "Kept on the product for reference — quotes price labor from your crew rate and hours",
    syn: ["labor per ft", "labor / ft", "labor per foot", "labor cost per ft", "install labor", "labor", "labor cost"] },
  { key: "sku", label: "SKU", syn: ["sku", "item #", "item number", "part number", "part #", "product code", "code"] },
  { key: "supplier", label: "Supplier", syn: ["supplier", "vendor", "manufacturer", "brand", "preferred vendor"] },
  { key: "notes", label: "Notes", syn: ["notes", "note", "comments", "purchase description", "sales description", "details", "description"] },
];

function fieldsFor(kind: string): any[] {
  return kind === "jobs" ? JOB_FIELDS : kind === "products" ? PRODUCT_FIELDS : CUSTOMER_FIELDS;
}

/* normHeader, but "#" reads as "number" first — "Job #" is a job number, not
   a job, and normHeader alone would make the two the same word */
function hkey(h: any): string { return normHeader(String(h == null ? "" : h).replace(/#/g, " number ")); }

/* file headers → { fieldKey: header }. Exact synonym matches first, in field
   order, each header used once; then a loose pass for the few fields whose
   words are unmistakable ("Customer Email Address", "Cell #"). */
function autoMap(kind: string, headers: string[]): Record<string, string> {
  const fields = fieldsFor(kind);
  const byNorm: Record<string, string> = {};
  (headers || []).forEach((h) => { const n = hkey(h); if (n && !(n in byNorm)) byNorm[n] = h; });
  const used = new Set<string>();
  const out: Record<string, string> = {};
  for (const f of fields) {
    for (const s of f.syn) {
      const h = byNorm[hkey(s)];
      if (h && !used.has(h)) { out[f.key] = h; used.add(h); break; }
    }
  }
  const LOOSE: Record<string, { has: string[]; not?: string[] }> = {
    email: { has: ["email", "mail"], not: ["opt", "consent", "subscribe", "marketing", "verified", "bounced"] },
    phone: { has: ["phone", "mobile", "cell"], not: ["fax", "opt", "consent", "sms", "ext", "type"] },
    zip: { has: ["zip", "postal"] },
    city: { has: ["city"], not: ["ethnicity", "capacity"] },
  };
  for (const f of fields) {
    const rule = LOOSE[f.key];
    if (!rule || out[f.key]) continue;
    const h = (headers || []).find((x) => {
      if (used.has(x)) return false;
      const n = normHeader(x);
      return rule.has.some((w) => n.includes(w)) && !(rule.not || []).some((w) => n.includes(w));
    });
    if (h) { out[f.key] = h; used.add(h); }
  }
  // Jobber-style files carry a service AND a billing address: a second street
  // line from the other one would glue two addresses together
  const fam = (h: any) => { const m = /^(service|property|billing|bill|mailing|job|site)/.exec(hkey(h)); return m ? m[1] : ""; };
  if (out.street && out.street2 && fam(out.street) !== fam(out.street2)) delete out.street2;
  return out;
}

/* ---- value helpers ---- */
function cellOf(row: any, map: any, key: string): string {
  const h = map && map[key];
  if (!h || !row) return "";
  const v = row[h];
  return String(v == null ? "" : v).replace(/\s+/g, " ").trim();
}
function normName(s: any): string { return String(s || "").trim().toLowerCase().replace(/[^a-z0-9& ]/g, "").replace(/\s+/g, " "); }
function validEmail(s: any): boolean { return /^[^\s@,;<>()]+@[^\s@,;<>()]+\.[a-z]{2,}$/i.test(String(s || "").trim()); }
/* "Pat <pat@x.com>, sam@y.com" → ["pat@x.com", "sam@y.com"] (lowercased) */
function emailsIn(raw: any): string[] {
  const found = String(raw || "").match(/[^\s@,;<>()"']+@[^\s@,;<>()"']+/g) || [];
  return found.map((e) => e.toLowerCase().replace(/[.,;]+$/, ""));
}
/* digits only; an 11-digit number starting with 1 (US country code) → 10 */
function phoneDigits(s: any): string {
  const d = String(s || "").replace(/\D/g, "");
  return d.length === 11 && d[0] === "1" ? d.slice(1) : d;
}
function formatPhone(s: any): string {
  const raw = String(s || "").trim();
  const ext = /(?:ext\.?|x)\s*(\d+)\s*$/i.exec(raw);
  const d = phoneDigits(ext ? raw.slice(0, ext.index) : raw);
  if (d.length !== 10) return raw;
  return "(" + d.slice(0, 3) + ") " + d.slice(3, 6) + "-" + d.slice(6) + (ext ? " x" + ext[1] : "");
}
/* one cell can hold several numbers ("555-0101, 555-0102" · Jobber's "#s" columns) */
function splitPhones(raw: any): string[] {
  return String(raw || "").split(/[,;|\n]|\s\/\s|\s+or\s+/i).map((x) => x.replace(/^\s*(mobile|cell|home|work|main|phone|m|h|w)\s*[:\-]\s*/i, "").trim()).filter((x) => phoneDigits(x).length >= 7);
}
/* "VIP, Repeat" · "VIP; Repeat" · Google's "* myContacts ::: Clients" */
function splitTags(raw: any): string[] {
  const out: string[] = [];
  String(raw || "").split(/:::|[,;|]/).map((t) => t.trim()).forEach((t) => {
    if (!t || t.startsWith("*")) return;
    if (!out.some((x) => x.toLowerCase() === t.toLowerCase())) out.push(t);
  });
  return out;
}
/* the estimator's lead-source keys (Reports → lead sources reads them) */
const SOURCE_LABELS: Record<string, string> = {
  referral: "Referral", google: "Google / LSA", website: "Website / widget", widget: "Website / widget", booking: "Online booking",
  social: "Facebook / Instagram", repeat: "Repeat customer", yardsign: "Yard sign", homeshow: "Home show", other: "Other",
};
function mapSource(raw: any): { key: string; detail: string } | null {
  const s = String(raw || "").trim();
  if (!s) return null;
  const t = s.toLowerCase();
  let key = "other";
  if (/refer|word of mouth|friend|neighbou?r|family|wom\b/.test(t)) key = "referral";
  else if (/repeat|past customer|existing customer|return(ing)? customer|previous customer/.test(t)) key = "repeat";
  else if (/google|\blsa\b|local services|search|seo|adwords|\bppc\b|gmb|business profile|maps|bing/.test(t)) key = "google";
  else if (/facebook|\bfb\b|instagram|\big\b|social|nextdoor|tiktok|youtube/.test(t)) key = "social";
  else if (/web|online|widget|internet|site|form/.test(t)) key = "website";
  else if (/yard sign|sign|drive[- ]?by|truck|vehicle|wrap/.test(t)) key = "yardsign";
  else if (/home show|show|expo|event|fair|parade/.test(t)) key = "homeshow";
  const label = (SOURCE_LABELS[key] || "").toLowerCase();
  return { key, detail: t === key || t === label ? "" : s };
}
/* same rule as the customer form: `address` stays the single-line source of truth */
function composeAddr(p: any): string {
  return [p.street, [p.city, p.state].filter(Boolean).join(", "), p.zip].filter(Boolean).join(" ").replace(/\s+,/g, ",").trim();
}
function streetKey(addr: any): string {
  return String(addr || "").split(",")[0].toLowerCase().replace(/[^a-z0-9]/g, "");
}
/* 1,234.5 · "12 ft" · "6'" → number; blank / unreadable → null */
function parseNum(raw: any): number | null {
  const s = String(raw == null ? "" : raw).trim().replace(/,/g, "");
  if (!s) return null;
  const m = /^-?\d*\.?\d+/.exec(s);
  if (!m) return null;
  const n = Number(m[0]);
  return Number.isFinite(n) ? n : null;
}
/* "6" · "6'" · "6 ft" · "72 in" · "72\"" · "4'6\"" → feet */
function parseHeightFt(raw: any): number | null {
  const s = String(raw || "").trim().toLowerCase();
  if (!s) return null;
  const ftIn = /^(\d+(?:\.\d+)?)\s*(?:'|ft|feet|foot)\s*(\d+(?:\.\d+)?)\s*(?:"|in|inch|inches)?$/.exec(s);
  if (ftIn) return Math.round((Number(ftIn[1]) + Number(ftIn[2]) / 12) * 100) / 100;
  const inch = /^(\d+(?:\.\d+)?)\s*(?:"|in|inch|inches)$/.exec(s);
  if (inch) return Math.round((Number(inch[1]) / 12) * 100) / 100;
  const n = parseNum(s);
  if (n == null || n <= 0) return null;
  return n > 20 ? Math.round((n / 12) * 100) / 100 : n;   // a bare 72 is inches
}
/* a loose date, also when a time rides along ("03/05/2024 2:22 PM", "2024-03-05 14:22") */
function parseDay(raw: any): string | null {
  const s = String(raw || "").trim();
  if (!s) return null;
  return parseDateLoose(s) || parseDateLoose(s.replace(/[ T]\d{1,2}:\d{2}(:\d{2}(\.\d+)?)?\s*([ap]\.?m\.?)?\s*(z|[+-]\d{2}:?\d{2}|[a-z]{2,4})?$/i, ""));
}
/* "2026-03-04" → local noon that day, in ms (noon: no timezone can roll it to another date) */
function dayToMs(ymd: any): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ""));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12).getTime() : null;
}
function msToDay(ms: any): string {
  const n = Number(ms);
  if (!(n > 0)) return "";
  const d = new Date(n);
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
}
function round2(n: any): number { return Math.round((Number(n) || 0) * 100) / 100; }

/* The job's stage from whatever the old system called it. Completed-like
   words → completed; scheduled-like words (Jobber's Upcoming / Today / Late,
   "in progress") → scheduled, but only with an install date; everything else
   → unscheduled. Cancelled / lost jobs are skipped, not imported as open work. */
function mapJobStatus(raw: any, has: { start?: boolean; completed?: boolean }): { status: string; skip?: string; note?: string } {
  const t = String(raw || "").trim().toLowerCase();
  if (/cancel|void|lost|declin|rejected|no[- ]?show/.test(t)) return { status: "", skip: "Cancelled — skipped" };
  if (/unschedul|not scheduled|(needs?|requires?) schedul|to be scheduled|\btbd\b/.test(t)) return { status: "unscheduled" };
  if (/complet|\bdone\b|closed|\bpaid\b|finish|invoic|archiv|\bwon\b/.test(t)) return { status: "completed" };
  if (/schedul|upcoming|today|\blate\b|booked|confirmed|progress|started|underway|active|ongoing/.test(t)) {
    return has.start ? { status: "scheduled" } : { status: "unscheduled", note: "No install date — imported as needs scheduling" };
  }
  if (!t) {
    if (has.completed) return { status: "completed" };
    if (has.start) return { status: "scheduled" };
  }
  return { status: "unscheduled" };
}

/* the engine's material categories (labor hours are set per category) */
const FENCE_CATS = ["Wood", "Vinyl", "Chain Link", "Iron/Aluminum", "Composite", "Farm/Ranch", "Industrial", "Temporary"];
function mapCategory(raw: any, alsoText?: any): { cat: string; guessed: boolean } {
  const test = (s: string) => {
    const t = s.toLowerCase();
    if (!t.trim()) return null;
    const exact = FENCE_CATS.find((c) => c.toLowerCase() === t.trim());
    if (exact) return exact;
    if (/chain|cyclone|\bcl\b/.test(t)) return "Chain Link";
    if (/vinyl|pvc|poly/.test(t)) return "Vinyl";
    if (/composite|trex|simtek|fiberon/.test(t)) return "Composite";
    if (/alum|iron|ornamental|steel|metal|wrought/.test(t)) return "Iron/Aluminum";
    if (/temp|rental|construction panel|crowd/.test(t)) return "Temporary";
    if (/industrial|security|358|anti[- ]?climb|palisade/.test(t)) return "Industrial";
    if (/farm|ranch|barb|field fence|woven wire|high[- ]tensile|t-post|livestock|agri/.test(t)) return "Farm/Ranch";
    if (/wood|cedar|pine|treated|\bpt\b|spruce|redwood|picket|privacy|split rail|board/.test(t)) return "Wood";
    return null;
  };
  const a = test(String(raw || ""));
  if (a) return { cat: a, guessed: false };
  const b = test(String(alsoText || ""));
  if (b) return { cat: b, guessed: !!String(raw || "").trim() };
  return { cat: "Wood", guessed: true };
}

/* ---- matching: who do we already have ---- */
function customerIndex(customers: any[]) {
  const byEmail = new Map<string, any>(), byPhone = new Map<string, any>(), byNameAddr = new Map<string, any>(), byName = new Map<string, any[]>();
  const add = (c: any) => {
    if (!c) return;
    const e = String(c.email || "").trim().toLowerCase();
    if (e && !byEmail.has(e)) byEmail.set(e, c);
    const p = phoneDigits(c.phone);
    if (p.length >= 10 && !byPhone.has(p)) byPhone.set(p, c);
    const n = normName(c.name);
    const a = streetKey(c.street || c.address);
    if (n && a && !byNameAddr.has(n + "|" + a)) byNameAddr.set(n + "|" + a, c);
    if (n) byName.set(n, [...(byName.get(n) || []), c]);
  };
  (customers || []).forEach(add);
  return { add, byEmail, byPhone, byNameAddr, byName };
}
/* email, then phone, then name + street. A bare name only counts when the row
   has nothing else to tell two people apart (no email, phone or address). */
function findCustomerMatch(idx: any, c: any): { rec: any; by: string } | null {
  if (c.email && idx.byEmail.has(c.email)) return { rec: idx.byEmail.get(c.email), by: "email" };
  const p = phoneDigits(c.phone);
  if (p.length >= 10 && idx.byPhone.has(p)) return { rec: idx.byPhone.get(p), by: "phone" };
  const n = normName(c.name), a = streetKey(c.street || c.address);
  if (n && a && idx.byNameAddr.has(n + "|" + a)) return { rec: idx.byNameAddr.get(n + "|" + a), by: "name and address" };
  if (n && !c.email && !p && !a) {
    const same = idx.byName.get(n) || [];
    if (same.length) return { rec: same[0], by: "name" };
  }
  return null;
}

/* ---- rows → what they'll become ---- */
function readCustomerRow(row: any, map: any) {
  const v = (k: string) => cellOf(row, map, k);
  const errors: string[] = [], warnings: string[] = [];
  const first = v("firstName"), last = v("lastName"), company = v("company");
  const name = v("name") || [first, last].filter(Boolean).join(" ") || company;
  if (!name) errors.push("No name");
  const emailRaw = v("email");
  const emails = emailsIn(emailRaw);
  const email = emails.find(validEmail) || "";
  if (emailRaw && !email) warnings.push(`Email "${emailRaw.slice(0, 40)}" doesn't look right — left off`);
  const phones = [...splitPhones(v("phone")), ...splitPhones(v("phone2"))];
  const phone = phones.length ? formatPhone(phones[0]) : (v("phone") ? v("phone") : "");
  if (v("phone") && !phones.length) warnings.push(`Phone "${v("phone").slice(0, 30)}" doesn't look right — kept as typed`);
  else if (phone && phoneDigits(phone).length !== 10 && !/\+/.test(phone)) warnings.push(`Phone "${phone}" isn't 10 digits`);
  const extras: string[] = [];
  phones.slice(1).forEach((p) => extras.push("Other phone: " + formatPhone(p)));
  emails.filter((e) => e !== email && validEmail(e)).forEach((e) => extras.push("Other email: " + e));
  const street = [v("street"), v("street2")].filter(Boolean).join(", ");
  const city = v("city"), zip = v("zip");
  let state = v("state");
  if (/^[a-z]{2}$/i.test(state)) state = state.toUpperCase();
  const parts = !!(street || city || state || zip);
  const address = parts ? composeAddr({ street, city, state, zip }) : v("address");
  const tags = splitTags(v("tags"));
  const src = mapSource(v("source"));
  const createdRaw = v("created");
  const since = parseDay(createdRaw);
  if (createdRaw && !since) warnings.push(`Couldn't read the date "${createdRaw.slice(0, 24)}" — left off`);
  const notes = [v("notes"), ...extras].filter(Boolean).join("\n");
  return {
    errors, warnings,
    c: { name, email, phone, street: parts ? street : "", city, state, zip, parts, address, tags, source: src ? src.key : "", sourceDetail: src ? src.detail : "", since: since || "", notes, company: company && company !== name ? company : "" },
  };
}

function newCustomerRecord(c: any, ctx: any): any {
  const rec: any = { id: ctx.newId("cust-"), name: c.name, phone: c.phone || "", email: c.email || "", address: c.address || "", notes: c.notes || "", createdAt: ctx.now,
    imported: { batchId: ctx.batchId, source: "csv", at: ctx.now } };
  if (c.parts) Object.assign(rec, { street: c.street || "", city: c.city || "", state: c.state || "", zip: c.zip || "" });
  if (c.tags && c.tags.length) rec.tags = c.tags.slice();
  if (c.source) rec.source = c.source;
  if (c.sourceDetail) rec.sourceDetail = c.sourceDetail;
  if (c.since) rec.customerSince = c.since;
  if (c.company) rec.company = c.company;
  return rec;
}

/* the blanks on `ex` that `c` can fill — never overwrites (upsertCustomerInList's rule) */
function customerFill(ex: any, c: any): { fields: Record<string, any>; tags: string[] } {
  const fields: Record<string, any> = {};
  const blank = (k: string) => !String((ex && ex[k]) || "").trim();
  if (c.phone && blank("phone")) fields.phone = c.phone;
  if (c.email && blank("email")) fields.email = c.email;
  if (c.address && blank("address") && blank("street")) {
    fields.address = c.address;
    if (c.parts) { if (c.street) fields.street = c.street; if (c.city) fields.city = c.city; if (c.state) fields.state = c.state; if (c.zip) fields.zip = c.zip; }
  }
  if (c.notes && blank("notes")) fields.notes = c.notes;
  if (c.source && blank("source")) { fields.source = c.source; if (c.sourceDetail && blank("sourceDetail")) fields.sourceDetail = c.sourceDetail; }
  if (c.since && blank("customerSince")) fields.customerSince = c.since;
  if (c.company && blank("company")) fields.company = c.company;
  const have = ((ex && ex.tags) || []).map((t: any) => String(t).toLowerCase());
  const tags = (c.tags || []).filter((t: string) => !have.includes(t.toLowerCase()));
  return { fields, tags };
}

function emptyPlan(kind: string, ctx: any) {
  return { kind, batchId: ctx.batchId, at: ctx.now, rows: [] as any[], customers: [] as any[], workOrders: [] as any[], products: [] as any[],
    updates: { customers: [] as any[], products: [] as any[] },
    counts: { create: 0, update: 0, skip: 0, error: 0, limit: 0, linkNew: 0 } };
}
function tally(plan: any) {
  const c = { create: 0, update: 0, skip: 0, error: 0, limit: plan.counts.limit || 0, linkNew: plan.counts.linkNew || 0 };
  plan.rows.forEach((r: any) => { c[r.action] = (c[r.action] || 0) + 1; });
  plan.counts = c;
  return plan;
}

/* ctx: { customers, fillBlanks, now, batchId, newId } */
function planCustomers(rows: any[], map: any, ctx: any) {
  const plan = emptyPlan("customers", ctx);
  const idx = customerIndex(ctx.customers || []);
  const mine = customerIndex([]);          // rows already taken from this file
  const rowOf = new Map<any, number>();
  const fillsById = new Map<string, any>();
  (rows || []).forEach((row, i) => {
    const line = i + 2;   // the header is line 1
    const { errors, warnings, c } = readCustomerRow(row, map);
    const r: any = { line, action: "create", errors, warnings, view: c };
    plan.rows.push(r);
    if (errors.length) { r.action = "error"; return; }
    const inFile = findCustomerMatch(mine, c);
    if (inFile) { r.action = "skip"; r.note = `Same customer as row ${rowOf.get(inFile.rec)} (${inFile.by})`; return; }
    const hit = findCustomerMatch(idx, c);
    if (hit) {
      r.match = { id: hit.rec.id, name: hit.rec.name, by: hit.by };
      if (!ctx.fillBlanks) { r.action = "skip"; r.note = `Already a customer: ${hit.rec.name || "(no name)"} — matched by ${hit.by}`; return; }
      // a second row for the same existing customer fills what the first left blank
      const prior = fillsById.get(hit.rec.id);
      const base = prior ? { ...hit.rec, ...prior.fields, tags: [...(hit.rec.tags || []), ...prior.tags] } : hit.rec;
      const fill = customerFill(base, c);
      if (!Object.keys(fill.fields).length && !fill.tags.length) { r.action = "skip"; r.note = `Already a customer: ${hit.rec.name || "(no name)"} — nothing new to add`; return; }
      r.action = "update";
      r.fill = fill;
      r.note = `Fills in ${[...Object.keys(fill.fields).filter((k) => !["street", "city", "state", "zip", "sourceDetail"].includes(k)).map(fieldWord), ...(fill.tags.length ? ["tags"] : [])].join(", ")} on ${hit.rec.name || "their record"} (matched by ${hit.by})`;
      if (prior) { Object.assign(prior.fields, fill.fields); prior.tags.push(...fill.tags); }
      else { const u = { id: hit.rec.id, fields: { ...fill.fields }, tags: fill.tags.slice() }; fillsById.set(hit.rec.id, u); plan.updates.customers.push(u); }
      return;
    }
    const rec = newCustomerRecord(c, ctx);
    r.rec = rec;
    plan.customers.push(rec);
    mine.add(rec); rowOf.set(rec, line);
  });
  return tally(plan);
}
function fieldWord(k: string): string {
  return ({ customerSince: "customer since", source: "lead source", notes: "notes", phone: "phone", email: "email", address: "address", company: "company" } as any)[k] || k;
}

/* ctx: { customers, workOrders, now, batchId, newId, activeCrewId, costs } */
function planJobs(rows: any[], map: any, ctx: any) {
  const plan = emptyPlan("jobs", ctx);
  const idx = customerIndex(ctx.customers || []);
  const jobKeys = new Set<string>(), refs = new Set<string>();
  const keyOf = (name: any, day: any, total: any, title: any) => normName(name) + "|" + (day || "") + "|" + Math.round(Number(total) || 0) + "|" + (day || Number(total) ? "" : normName(title));
  (ctx.workOrders || []).forEach((w: any) => {
    if (!w) return;
    jobKeys.add(keyOf(w.customer, w.scheduledStart || msToDay(w.completedAt), w.total, w.title));
    if (w.sourceRef) refs.add(String(w.sourceRef).toLowerCase());
  });
  const fileKeys = new Map<string, number>(), fileRefs = new Map<string, number>();
  (rows || []).forEach((row, i) => {
    const line = i + 2;
    const v = (k: string) => cellOf(row, map, k);
    const errors: string[] = [], warnings: string[] = [];
    const name = v("name") || [v("firstName"), v("lastName")].filter(Boolean).join(" ");
    if (!name) errors.push("No customer name");
    const emails = emailsIn(v("email"));
    const email = emails.find(validEmail) || "";
    if (v("email") && !email) warnings.push(`Email "${v("email").slice(0, 40)}" doesn't look right — left off`);
    const phones = splitPhones(v("phone"));
    const phone = phones.length ? formatPhone(phones[0]) : "";
    const street = v("street"), city = v("city"), zip = v("zip");
    let state = v("state"); if (/^[a-z]{2}$/i.test(state)) state = state.toUpperCase();
    const parts = !!(street || city || state || zip);
    const address = parts ? composeAddr({ street, city, state, zip }) : v("address");
    const readDay = (k: string, label: string) => {
      const raw = v(k); if (!raw) return null;
      const d = parseDay(raw);
      if (!d) warnings.push(`Couldn't read the ${label} "${raw.slice(0, 24)}" — left off`);
      return d;
    };
    const start = readDay("start", "install date"), end = readDay("end", "end date"), done = readDay("completed", "completed date");
    const readMoney = (k: string, label: string) => {
      const raw = v(k); if (!raw) return null;
      const n = parseMoney(raw);
      if (n == null) warnings.push(`Couldn't read the ${label} "${raw.slice(0, 20)}" — left off`);
      else if (n < 0) { warnings.push(`${label[0].toUpperCase() + label.slice(1)} is negative — left off`); return null; }
      return n;
    };
    const total = readMoney("total", "contract value");
    if (total == null && map && map.total && !v("total")) warnings.push("No contract value — imported at $0");
    const st = mapJobStatus(v("status"), { start: !!start, completed: !!done });
    const status = st.status;
    const rawTitle = v("title");
    const title = rawTitle ? (normName(rawTitle).includes(normName(name)) ? rawTitle : name + " — " + rawTitle) : (name || "Job") + " — Fence job";
    // the dates the job will carry: a completed job without an install date is
    // placed on its completion day; an unscheduled one carries none
    const doneDay = status === "completed" ? done || end || start : null;
    const schedStart = status === "completed" ? start || doneDay || "" : status === "scheduled" ? start || "" : "";
    const schedEnd = status === "completed" ? end || start || doneDay || "" : status === "scheduled" ? end || start || "" : "";
    const r: any = { line, action: "create", errors, warnings, view: { name, email, phone, address, title, status, start: schedStart, done: doneDay, total } };
    plan.rows.push(r);
    if (errors.length) { r.action = "error"; return; }
    if (st.skip) { r.action = "skip"; r.note = st.skip; return; }
    if (st.note) warnings.push(st.note);
    const ref = v("jobNumber");
    const key = keyOf(name, schedStart || doneDay, total != null ? round2(total) : 0, title);
    if (ref && fileRefs.has(ref.toLowerCase())) { r.action = "skip"; r.note = `Same job number as row ${fileRefs.get(ref.toLowerCase())}`; return; }
    if (!ref && fileKeys.has(key)) { r.action = "skip"; r.note = `Same job as row ${fileKeys.get(key)}`; return; }
    if ((ref && refs.has(ref.toLowerCase())) || jobKeys.has(key)) { r.action = "skip"; r.note = "Already in your jobs"; return; }
    if (ref) fileRefs.set(ref.toLowerCase(), line); else fileKeys.set(key, line);

    // the customer: link to the one we have, else add them with this import
    const cand = { name, email, phone, street, address };
    const hit = findCustomerMatch(idx, cand) || (() => {
      const same = idx.byName.get(normName(name)) || [];
      if (!same.length) return null;
      if (same.length > 1) warnings.push(`${same.length} customers are named ${name} — linked to the first`);
      return { rec: same[0], by: "name" };
    })();
    let cust = hit && hit.rec;
    if (!cust) {
      cust = newCustomerRecord({ name, email, phone, street: parts ? street : "", city, state, zip, parts, address, tags: [], notes: "" }, ctx);
      plan.customers.push(cust);
      idx.add(cust);
      plan.counts.linkNew++;
      r.newCustomer = true;
    }
    r.match = { id: cust.id, name: cust.name, by: hit ? hit.by : "new" };

    const lf = parseNum(v("lf"));
    const hours = parseNum(v("hours"));
    if (v("hours") && hours == null) warnings.push(`Couldn't read the hours "${v("hours").slice(0, 16)}" — left off`);
    const laborCost = ctx.costs ? readMoney("laborCost", "labor cost") : null;
    const materialCost = ctx.costs ? readMoney("materialCost", "material cost") : null;
    const wo: any = {
      id: ctx.newId("wo-"), customerId: cust.id, customer: name, address: address || cust.address || "",
      title, total: total != null ? round2(total) : 0, status,
      scheduledStart: schedStart, scheduledEnd: schedEnd,
      suppliesOrdered: false, materialEta: "", priority: false, crewHours: null,
      notes: v("notes"), createdAt: ctx.now,
      imported: { batchId: ctx.batchId, source: "csv", at: ctx.now },
    };
    if (status !== "completed" && ctx.activeCrewId) wo.crewId = ctx.activeCrewId;
    if (status === "completed") {
      if (doneDay) wo.completedAt = dayToMs(doneDay);
      if (!done) warnings.push(doneDay ? "No completed date — dated from the install date" : "No completed or install date — reports can't place it in a month");
    }
    if (status === "unscheduled" && (start || end)) warnings.push("Not scheduled — the date in the file is left off");
    if (lf != null && lf > 0) wo.lf = lf;
    if (hours != null && hours > 0 && status !== "completed") wo.crewHours = hours;
    const act: any = {};
    if (laborCost != null) act.laborCost = round2(laborCost);
    if (materialCost != null) act.materialCost = round2(materialCost);
    if (hours != null && hours > 0) act.hours = hours;
    if (Object.keys(act).length) wo.importedActuals = act;
    if (ref) wo.sourceRef = ref;
    r.rec = wo;
    plan.workOrders.push(wo);
  });
  return tally(plan);
}

/* what a product row says, before it meets the price book.
   opts: { markup, markupBase, laborPerFtFor(cat), panelLenFor(cat) } */
function readProductRow(row: any, map: any, opts: any) {
  const v = (k: string) => cellOf(row, map, k);
  const errors: string[] = [], warnings: string[] = [];
  const name = v("name");
  if (!name) errors.push("No product name");
  const catRaw = v("category");
  const { cat, guessed } = mapCategory(catRaw, [name, v("style")].join(" "));
  if (guessed) warnings.push(catRaw ? `Material "${catRaw.slice(0, 20)}" not recognised — set to ${cat}` : `Couldn't tell the material — set to ${cat}`);
  const money = (k: string, label: string) => {
    const raw = v(k); if (!raw) return null;
    const n = parseMoney(raw);
    if (n == null || n < 0) { warnings.push(`Couldn't read the ${label} "${raw.slice(0, 20)}" — left off`); return null; }
    return n;
  };
  const matPerFt = money("matPerFt", "material cost / ft");
  const matPerSection = money("matPerSection", "material cost / section");
  const postCost = money("postCost", "post cost");
  const gateCost = money("gateCost", "gate cost");
  const sellPerFt = money("sellPerFt", "customer price / ft");
  const laborPerFt = money("laborPerFt", "labor / ft");
  const secRaw = parseNum(v("sectionLen"));
  const sectionLen = secRaw != null && secRaw > 0 && secRaw <= 30 ? secRaw : null;
  if (v("sectionLen") && sectionLen == null) warnings.push(`Section length "${v("sectionLen").slice(0, 12)}" isn't a length in feet — used the ${cat} default`);
  const heightRaw = v("height");
  const heightFt = parseHeightFt(heightRaw);
  if (heightRaw && heightFt == null) warnings.push(`Couldn't read the height "${heightRaw.slice(0, 12)}"`);
  const markup = Number(opts && opts.markup) > 0 ? Number(opts.markup) : 2;
  const laborFt = opts && opts.laborPerFtFor ? Number(opts.laborPerFtFor(cat)) || 0 : 0;
  const onMaterial = opts && opts.markupBase === "material";
  const panelWidth = sectionLen || (opts && opts.panelLenFor ? Number(opts.panelLenFor(cat)) || 8 : 8);
  let panelCost: number | null = null, costNote = "";
  if (matPerSection != null) panelCost = round2(matPerSection);
  else if (matPerFt != null) panelCost = round2(matPerFt * panelWidth);
  else if (sellPerFt != null) {
    const ft = sellPerFt / markup - (onMaterial ? 0 : laborFt);
    if (ft <= 0) errors.push(`At your ×${markup} markup, $${round2(sellPerFt)}/ft doesn't cover the ${cat.toLowerCase()} labor — add a material cost column`);
    else { panelCost = round2(ft * panelWidth); costNote = `No cost in the file — material worked out from the $${round2(sellPerFt)}/ft price at your ×${markup} markup`; }
  }
  if (costNote) warnings.push(costNote);
  const matFt = panelCost != null ? panelCost / panelWidth + (postCost != null ? postCost / panelWidth : 0) : null;
  if (matFt != null && sellPerFt != null && !costNote) {
    const est = (matFt + (onMaterial ? 0 : laborFt)) * markup;
    if (sellPerFt > 0 && Math.abs(est - sellPerFt) / sellPerFt > 0.25) warnings.push(`Your sheet says $${round2(sellPerFt)}/ft; at your ×${markup} markup the fence line prices near $${round2(est)}/ft — check your markup`);
  }
  const style = v("style"), color = v("color");
  return {
    errors, warnings,
    p: { name, category: cat, style, color, panelWidth, panelCost, postCost, gateCost, sellPerFt, laborPerFt, heightFt, sku: v("sku"), supplier: v("supplier"), notes: v("notes"),
      hasCost: panelCost != null,
      // the panel-only material per foot, and whether the file said how long a section is
      perFt: panelCost != null ? panelCost / panelWidth : null, bySection: matPerSection != null || sectionLen != null },
  };
}

/* opts adds: { allProducts, customProducts, updateExisting, limit, now, batchId, newId, colorFor(cat) } */
function planProducts(rows: any[], map: any, opts: any) {
  const plan = emptyPlan("products", opts);
  const custom = opts.customProducts || [];
  const customIds = new Set(custom.map((p: any) => p && p.id));
  const byName = new Map<string, any>();
  // your own products first: a custom override of a built-in is the one in use
  [...custom, ...(opts.allProducts || []).filter((p: any) => p && !customIds.has(p.id))].forEach((p: any) => {
    const n = normName(p && p.name);
    if (n && !byName.has(n)) byName.set(n, p);
  });
  const cap = Math.min(Number.isFinite(Number(opts.limit)) ? Number(opts.limit) : Infinity, PRICEBOOK_MAX);
  let room = Math.max(0, cap - custom.length);
  const seen = new Map<string, number>();
  (rows || []).forEach((row, i) => {
    const line = i + 2;
    const { errors, warnings, p } = readProductRow(row, map, opts);
    const r: any = { line, action: "create", errors, warnings, view: p };
    plan.rows.push(r);
    if (errors.length) { r.action = "error"; return; }
    const n = normName(p.name);
    if (seen.has(n)) { r.action = "skip"; r.note = `Same product as row ${seen.get(n)}`; return; }
    seen.set(n, line);
    const ex = byName.get(n);
    if (ex) {
      r.match = { id: ex.id, name: ex.name, by: "name" };
      if (!opts.updateExisting) { r.action = "skip"; r.note = `Already in your products: ${ex.name}`; return; }
      const ed = ex._editor;
      if (ed && Array.isArray(ed.components) && ed.components.length && !ed.flatRate) {
        r.action = "skip"; r.note = `${ex.name} is priced from its own component list — change it in Products`; return;
      }
      const set = productSet(ex, p, opts.now);
      const changed = Object.keys(set).filter((k) => k !== "costUpdatedAt");
      if (!changed.length) { r.action = "skip"; r.note = `${ex.name} already has these numbers`; return; }
      if (customIds.has(ex.id)) {
        r.action = "update";
        r.note = `Updates ${changed.map(setWord).filter((x, k, a) => a.indexOf(x) === k).join(", ")} on ${ex.name}`;
        r.rec = applySet(JSON.parse(JSON.stringify(ex)), set);
        plan.updates.products.push({ id: ex.id, set });
        return;
      }
      // a built-in: the update becomes your own copy of it (same id — it replaces the built-in)
      if (room <= 0) { r.action = "skip"; r.limit = true; r.note = "Over your plan's product limit"; plan.counts.limit++; return; }
      room--;
      const rec = applySet(JSON.parse(JSON.stringify(ex)), set);
      rec.custom = true;
      rec.imported = { batchId: opts.batchId, source: "csv", at: opts.now };
      rec.importOverride = true;
      r.action = "update";
      r.note = `Makes your own copy of the built-in ${ex.name} with these numbers`;
      r.rec = rec;
      plan.products.push(rec);
      return;
    }
    if (!p.hasCost) { r.action = "error"; errors.push("No cost or price — nothing to price it from"); return; }
    if (room <= 0) { r.action = "skip"; r.limit = true; r.note = cap === PRICEBOOK_MAX && custom.length + plan.products.length >= PRICEBOOK_MAX ? `Imports stop at ${PRICEBOOK_MAX} products in the price book` : "Over your plan's product limit"; plan.counts.limit++; return; }
    room--;
    const rec: any = {
      id: opts.newId("cp-"), name: p.name, sub: [p.color, p.style].filter(Boolean).join(" · ") || "Imported", category: p.category,
      color: opts.colorFor ? opts.colorFor(p.category) : "#9aa6b8",
      panelWidth: p.panelWidth, panelCost: p.panelCost, postKit: p.postCost != null ? round2(p.postCost) : 0,
      sleeveCost: 0, centering: 0, adhesiveCost: 0, adhesivePerPosts: 999, concretePerBag: 0, bagsPerPost: 0,
      allSteel: false, custom: true, costUpdatedAt: opts.now,
      imported: { batchId: opts.batchId, source: "csv", at: opts.now },
    };
    if (p.gateCost != null) rec.gateCost = round2(p.gateCost); else warnings.push("No gate cost in the file");
    if (p.heightFt != null) rec.heightFt = p.heightFt;
    if (p.sellPerFt != null) rec.sellPerFt = round2(p.sellPerFt);
    if (p.laborPerFt != null) rec.laborPerFt = round2(p.laborPerFt);
    if (p.sku) rec.sku = p.sku;
    if (p.supplier) rec.supplier = p.supplier;
    if (p.notes) rec.notes = p.notes;
    r.rec = rec;
    plan.products.push(rec);
  });
  return tally(plan);
}
/* the fields a sheet row changes on an existing product, as dotted paths →
   new values. Only what the row has AND differs; a flat-rate editor product
   keeps its per-foot figure in _editor.flatPerFt, which is what the engine
   prices. A gate list or tier prices on the product win over the flat
   gateCost, so gateCost is left alone there. */
function productSet(ex: any, p: any, now: any): Record<string, any> {
  const set: Record<string, any> = {};
  const put = (k: string, val: any) => { if (val == null || val === "") return; if (JSON.stringify(getPath(ex, k)) !== JSON.stringify(val)) set[k] = val; };
  const ed = ex && ex._editor;
  if (p.hasCost) {
    const w = Number(ex && ex.panelWidth) || p.panelWidth;
    if (ed && ed.flatRate) {
      const perFt = round2(p.perFt);
      put("_editor.flatPerFt", perFt);
      put("panelCost", round2(perFt * w));
    } else if (p.bySection) {
      put("panelCost", p.panelCost);
      put("panelWidth", p.panelWidth);
    } else {
      put("panelCost", round2(p.perFt * w));   // a per-foot figure keeps the product's own section length
    }
    if (p.postCost != null && !(ed && ed.flatRate)) put("postKit", round2(p.postCost));
  }
  const gates = ed && ed.gates;
  const gateListed = gates && ((Array.isArray(gates.list) && gates.list.some((g: any) => Number(g && g.cost) > 0)) || ["walk", "drive10", "drive12"].some((k) => Number(gates[k]) > 0));
  if (p.gateCost != null && !gateListed) put("gateCost", round2(p.gateCost));
  if (p.heightFt != null) put("heightFt", p.heightFt);
  if (p.sellPerFt != null) put("sellPerFt", round2(p.sellPerFt));
  if (p.laborPerFt != null) put("laborPerFt", round2(p.laborPerFt));
  if (p.sku) put("sku", p.sku);
  if (p.supplier) put("supplier", p.supplier);
  if (p.notes) put("notes", p.notes);
  if (Object.keys(set).length) set.costUpdatedAt = now;
  return set;
}
function setWord(k: string): string {
  return ({ panelCost: "material cost", "_editor.flatPerFt": "material cost", panelWidth: "section length", postKit: "post cost", gateCost: "gate cost", heightFt: "height", sellPerFt: "price", laborPerFt: "labor", sku: "SKU", supplier: "supplier", notes: "notes" } as any)[k] || k;
}
function getPath(o: any, path: string): any {
  return String(path).split(".").reduce((x: any, k: string) => (x == null ? undefined : x[k]), o);
}
/* returns a copy of `o` with the dotted path set (undefined deletes it) */
function setPath(o: any, path: string, val: any): any {
  const keys = String(path).split(".");
  const out = Array.isArray(o) ? o.slice() : { ...(o || {}) };
  if (keys.length === 1) { if (val === undefined) delete out[keys[0]]; else out[keys[0]] = val; return out; }
  out[keys[0]] = setPath(out[keys[0]], keys.slice(1).join("."), val);
  return out;
}
function applySet(o: any, set: Record<string, any>): any {
  return Object.keys(set).reduce((x, k) => setPath(x, k, set[k]), o);
}

/* the rows the preview shows: the first PREVIEW_ROWS, then any later row that
   errors or is skipped (capped), so nothing dropped goes unseen */
function previewRows(plan: any, onlyProblems?: boolean) {
  const rows = plan.rows || [];
  if (onlyProblems) return rows.filter((r: any) => r.action === "error" || r.action === "skip" || r.warnings.length).slice(0, 200);
  return rows.slice(0, PREVIEW_ROWS);
}

/* ---- saving it: one state write, and taking it back ---- */
/* plan → what the store writes. `sample` marks a sandbox sample import: its
   records carry demoSeed so "Keep this as my free workspace" leaves them behind. */
function batchFromPlan(plan: any, opts?: { sample?: boolean }) {
  const mark = (x: any) => (opts && opts.sample ? { ...x, demoSeed: true } : x);
  return {
    kind: plan.kind, batchId: plan.batchId, at: plan.at,
    customers: plan.customers.map(mark), workOrders: plan.workOrders.map(mark), products: plan.products.map(mark),
    updates: { customers: plan.updates.customers, products: plan.updates.products },
  };
}

/* state + batch → next state. Updates are re-checked against the state as it
   is NOW (a blank filled in since the preview stays filled), and what each
   one actually changed is written on that record for undo. meta: { file, at,
   counts, sample } → one line in settings.importHistory. */
function applyImportBatch(s: any, batch: any, meta: any) {
  const id = batch.batchId, at = (meta && meta.at) || batch.at || Date.now();
  let customers = (s.customers || []).slice();
  const have = new Set(customers.map((c: any) => c && c.id));
  let updated = 0;
  const fills = new Map<string, any>((((batch.updates && batch.updates.customers) || []) as any[]).map((u: any) => [u.id, u]));
  if (fills.size) customers = customers.map((c: any) => {
    const u = c && fills.get(c.id);
    if (!u) return c;
    const done: Record<string, any> = {};
    const next: any = { ...c };
    Object.keys(u.fields || {}).forEach((k) => {
      if (String(next[k] || "").trim()) return;
      if (["street", "city", "state", "zip"].includes(k) && done.address === undefined) return;   // parts only ride with the address they belong to
      next[k] = u.fields[k]; done[k] = u.fields[k];
    });
    const had = (next.tags || []).map((t: any) => String(t).toLowerCase());
    const tags = (u.tags || []).filter((t: string) => !had.includes(t.toLowerCase()));
    if (tags.length) next.tags = [...(next.tags || []), ...tags];
    if (!Object.keys(done).length && !tags.length) return c;
    next.importFills = [...(Array.isArray(c.importFills) ? c.importFills : []), { batchId: id, at, fields: done, tags }];
    updated++;
    return next;
  });
  const newCustomers = (batch.customers || []).filter((c: any) => c && c.id && !have.has(c.id));
  customers = customers.concat(newCustomers);

  const woIds = new Set((s.workOrders || []).map((w: any) => w && w.id));
  const newWos = (batch.workOrders || []).filter((w: any) => w && w.id && !woIds.has(w.id));
  const workOrders = (s.workOrders || []).concat(newWos);

  let customProducts = (s.customProducts || []).slice();
  const pIds = new Set(customProducts.map((p: any) => p && p.id));
  const sets = new Map<string, any>((((batch.updates && batch.updates.products) || []) as any[]).map((u: any) => [u.id, u]));
  if (sets.size) customProducts = customProducts.map((p: any) => {
    const u = p && sets.get(p.id);
    if (!u) return p;
    const prev: Record<string, any> = {}, set: Record<string, any> = {};
    Object.keys(u.set || {}).forEach((k) => { prev[k] = getPath(p, k); set[k] = u.set[k]; });
    updated++;
    const next = applySet(p, set);
    next.importPrev = [...(Array.isArray(p.importPrev) ? p.importPrev : []), { batchId: id, at, set, prev }];
    return next;
  });
  const newProducts = (batch.products || []).filter((p: any) => p && p.id && !pIds.has(p.id));
  customProducts = customProducts.concat(newProducts);

  const created = newCustomers.length + newWos.length + newProducts.length;
  const entry: any = { id, kind: batch.kind, file: (meta && meta.file) || "", at,
    created, customers: newCustomers.length, jobs: newWos.length, products: newProducts.length, updated,
    skipped: (meta && meta.counts && (meta.counts.skip || 0) + (meta.counts.error || 0)) || 0 };
  if (meta && meta.sample) entry.sample = true;
  const history = [entry, ...(((s.settings && s.settings.importHistory) || []) as any[]).filter((h: any) => h && h.id !== id)].slice(0, HISTORY_KEEP);
  const what = batch.kind === "jobs" ? `${newWos.length} job${newWos.length === 1 ? "" : "s"}${newCustomers.length ? ` and ${newCustomers.length} new customer${newCustomers.length === 1 ? "" : "s"}` : ""}`
    : batch.kind === "products" ? `${newProducts.length} product${newProducts.length === 1 ? "" : "s"}`
    : `${newCustomers.length} customer${newCustomers.length === 1 ? "" : "s"}`;
  const text = `Imported ${what}${updated ? `, updated ${updated}` : ""}${entry.file ? " from " + entry.file : ""}`;
  return { ...s, customers, workOrders, customProducts,
    settings: { ...(s.settings || {}), importHistory: history },
    activity: [{ text, time: "Just now" }, ...(s.activity || [])].slice(0, 20) };
}

/* what an import still has in the state: records it created, and records it changed */
function importLeft(s: any, batchId: string) {
  const mine = (x: any) => !!(x && x.imported && x.imported.batchId === batchId);
  const touched = (x: any, key: string) => !!(x && Array.isArray(x[key]) && x[key].some((f: any) => f && f.batchId === batchId));
  const customers = (s.customers || []).filter(mine).length;
  const jobs = (s.workOrders || []).filter(mine).length;
  const products = (s.customProducts || []).filter(mine).length;
  const fills = (s.customers || []).filter((c: any) => touched(c, "importFills")).length + (s.customProducts || []).filter((p: any) => touched(p, "importPrev")).length;
  const custIds = new Set((s.customers || []).filter(mine).map((c: any) => c.id));
  const linked = [...(s.quotes || []), ...(s.projects || []), ...(s.workOrders || []).filter((w: any) => !mine(w))].filter((x: any) => x && x.customerId && custIds.has(x.customerId)).length;
  return { customers, jobs, products, created: customers + jobs + products, fills, linked, total: customers + jobs + products + fills };
}

/* Take an import back. Removes the records it created (at most `max` of each
   kind per call — see UNDO_SLICE) and, on every call, puts back the blanks it
   filled and the product numbers it changed, but only where the value is
   still the one the import wrote: an edit made since wins. Anything still
   pointing at a removed customer is unlinked and keeps the name, as
   deleteCustomer does. When nothing of the batch is left, its history line
   is marked undone. */
function revertImportBatch(s: any, batchId: string, opts?: { max?: number; maxProducts?: number; now?: number }) {
  const max = opts && opts.max > 0 ? opts.max : Infinity;
  const maxP = opts && opts.maxProducts > 0 ? opts.maxProducts : max;
  const now = (opts && opts.now) || Date.now();
  const mine = (x: any) => !!(x && x.imported && x.imported.batchId === batchId);
  const pick = (arr: any[], n: number) => { const ids = new Set<string>(); for (const x of arr || []) { if (ids.size >= n) break; if (mine(x)) ids.add(x.id); } return ids; };
  const goneC = pick(s.customers, max), goneW = pick(s.workOrders, max), goneP = pick(s.customProducts, maxP);
  const byId = new Map<string, any>((s.customers || []).filter((c: any) => c && goneC.has(c.id)).map((c: any) => [c.id, c]));
  const customers = (s.customers || []).filter((c: any) => !(c && goneC.has(c.id))).map((c: any) => {
    const list = Array.isArray(c && c.importFills) ? c.importFills : null;
    if (!list || !list.some((f: any) => f && f.batchId === batchId)) return c;
    const next: any = { ...c };
    list.filter((f: any) => f && f.batchId === batchId).forEach((f: any) => {
      Object.keys(f.fields || {}).forEach((k) => {
        if (next[k] !== f.fields[k]) return;   // edited since — theirs now
        if (["phone", "email", "address", "notes", "street", "city", "state", "zip"].includes(k)) next[k] = ""; else delete next[k];
      });
      if ((f.tags || []).length && Array.isArray(next.tags)) {
        const drop = new Set(f.tags.map((t: string) => t.toLowerCase()));
        next.tags = next.tags.filter((t: any) => !drop.has(String(t).toLowerCase()));
      }
    });
    const rest = list.filter((f: any) => !(f && f.batchId === batchId));
    if (rest.length) next.importFills = rest; else delete next.importFills;
    return next;
  });
  const unlink = (x: any) => {
    if (!(x && x.customerId && byId.has(x.customerId))) return x;
    const c = byId.get(x.customerId);
    return { ...x, customerId: null, customer: x.customer || c.name || "" };
  };
  const quotes = (s.quotes || []).map((q: any) => {
    if (!(q && q.customerId && byId.has(q.customerId))) return q;
    const c = byId.get(q.customerId);
    return { ...unlink(q), phone: q.phone || c.phone || "", email: q.email || c.email || "", address: q.address || c.address || "" };
  });
  const projects = (s.projects || []).map(unlink);
  const workOrders = (s.workOrders || []).filter((w: any) => !(w && goneW.has(w.id))).map(unlink);
  const customProducts = (s.customProducts || []).filter((p: any) => !(p && goneP.has(p.id))).map((p: any) => {
    const list = Array.isArray(p && p.importPrev) ? p.importPrev : null;
    if (!list || !list.some((u: any) => u && u.batchId === batchId)) return p;
    let next: any = p;
    list.filter((u: any) => u && u.batchId === batchId).forEach((u: any) => {
      Object.keys(u.set || {}).forEach((k) => {
        if (JSON.stringify(getPath(next, k)) === JSON.stringify(u.set[k])) next = setPath(next, k, (u.prev || {})[k]);
      });
    });
    const rest = list.filter((u: any) => !(u && u.batchId === batchId));
    next = { ...next };
    if (rest.length) next.importPrev = rest; else delete next.importPrev;
    return next;
  });
  const out: any = { ...s, customers, quotes, projects, workOrders, customProducts };
  const left = importLeft(out, batchId);
  const hist = ((s.settings && s.settings.importHistory) || []) as any[];
  const entry = hist.find((h: any) => h && h.id === batchId);
  if (left.total === 0 && entry && !entry.undoneAt) {
    out.settings = { ...(s.settings || {}), importHistory: hist.map((h: any) => (h && h.id === batchId ? { ...h, undoneAt: now } : h)) };
    out.activity = [{ text: `Undid the import${entry.file ? " of " + entry.file : ""}`, time: "Just now" }, ...(s.activity || [])].slice(0, 20);
  }
  return out;
}

/* ---- templates: the header row + one example, per import ---- */
const TEMPLATES: Record<string, { columns: string[]; example: any }> = {
  customers: {
    columns: ["Name", "Phone", "Email", "Street", "City", "State", "ZIP", "Tags", "Lead source", "Customer since", "Notes"],
    example: { "Name": "Pat Doe", "Phone": "(920) 555-0100", "Email": "pat.doe@example.com", "Street": "123 Main St", "City": "Green Bay", "State": "WI", "ZIP": "54301", "Tags": "Repeat; Residential", "Lead source": "Referral", "Customer since": "2023-05-14", "Notes": "Gate code 4411. Dog in the yard." },
  },
  jobs: {
    columns: ["Customer", "Email", "Phone", "Street", "City", "State", "ZIP", "Job", "Job number", "Status", "Install date", "Completed date", "Total", "Linear feet", "Labor cost", "Material cost", "Hours", "Notes"],
    example: { "Customer": "Pat Doe", "Email": "pat.doe@example.com", "Phone": "(920) 555-0100", "Street": "123 Main St", "City": "Green Bay", "State": "WI", "ZIP": "54301", "Job": "6 ft cedar privacy", "Job number": "1042", "Status": "Completed", "Install date": "2025-06-02", "Completed date": "2025-06-03", "Total": 7850, "Linear feet": 180, "Labor cost": 1900, "Material cost": 2650, "Hours": 38, "Notes": "Tear-out of old chain link." },
  },
  products: {
    columns: ["Name", "Material", "Style", "Height", "Material cost / ft", "Material cost / section", "Section length (ft)", "Post cost", "Gate cost", "Customer price / ft", "Labor / ft", "SKU", "Supplier", "Notes"],
    example: { "Name": "6 ft Cedar Dog Ear Privacy", "Material": "Wood", "Style": "Dog Ear Privacy", "Height": "6", "Material cost / ft": "", "Material cost / section": 96.5, "Section length (ft)": 8, "Post cost": 18, "Gate cost": 185, "Customer price / ft": 38, "Labor / ft": 9.5, "SKU": "CDR-6DE", "Supplier": "Lumber Yard Co", "Notes": "" },
  },
};
function templateRows(kind: string) {
  const t = TEMPLATES[kind] || TEMPLATES.customers;
  return { columns: t.columns.slice(), rows: [t.example] };
}

/* ---- exports: { columns, rows } for toCsv ---- */
const STAGE_LABEL: Record<string, string> = { materials: "Order Supplies", unscheduled: "Needs Scheduling", scheduled: "Scheduled", in_progress: "In Progress", completed: "Completed", done: "Completed", cancelled: "Cancelled" };

/* won work per customer: projects (quotes that became jobs) + jobs that never
   came from a quote (typed in, or imported) — the same job never counts twice */
function customerRollup(s: any) {
  const out = new Map<string, { jobs: number; won: number; firstSource: string; firstAt: number }>();
  const get = (id: string) => { let r = out.get(id); if (!r) { r = { jobs: 0, won: 0, firstSource: "", firstAt: Infinity }; out.set(id, r); } return r; };
  const nameToId = new Map<string, string>();
  (s.customers || []).forEach((c: any) => { const n = normName(c && c.name); if (n && !nameToId.has(n)) nameToId.set(n, c.id); });
  const owner = (x: any) => (x && x.customerId) || nameToId.get(normName(x && x.customer)) || null;
  (s.projects || []).forEach((p: any) => { const id = owner(p); if (!id) return; const r = get(id); r.jobs++; r.won += Number(p.total) || 0; });
  (s.workOrders || []).forEach((w: any) => {
    if (!w || w.projectId || w.quoteId || /^cancel/i.test(String(w.status || ""))) return;
    const id = owner(w); if (!id) return; const r = get(id); r.jobs++; r.won += Number(w.total) || 0;
  });
  (s.quotes || []).forEach((q: any) => {
    const id = owner(q); if (!id || !q.source) return; const r = get(id);
    const t = Number(q.createdAt) || 0;
    if (t < r.firstAt) { r.firstAt = t; r.firstSource = q.source; }
  });
  return out;
}

function customersExport(s: any) {
  const roll = customerRollup(s);
  const openQuotes = new Set((s.quotes || []).filter((q: any) => q && q.customerId && !["lost"].includes(q.status)).map((q: any) => q.customerId));
  const columns = [
    { key: "name", label: "Name" }, { key: "type", label: "Type" }, { key: "phone", label: "Phone" }, { key: "email", label: "Email" },
    { key: "street", label: "Street" }, { key: "city", label: "City" }, { key: "state", label: "State" }, { key: "zip", label: "ZIP" },
    { key: "address", label: "Full address" }, { key: "tags", label: "Tags" }, { key: "source", label: "Lead source" },
    { key: "created", label: "Customer since" }, { key: "jobs", label: "Jobs" }, { key: "ltv", label: "Lifetime value" }, { key: "notes", label: "Notes" },
  ];
  const rows = (s.customers || []).filter(Boolean).map((c: any) => {
    const r = roll.get(c.id) || { jobs: 0, won: 0, firstSource: "" };
    const src = c.source || r.firstSource || "";
    return {
      name: c.name || "", type: r.jobs > 0 ? "Customer" : openQuotes.has(c.id) ? "Lead" : "Contact",
      phone: c.phone || "", email: c.email || "", street: c.street || "", city: c.city || "", state: c.state || "", zip: c.zip || "",
      address: c.address || "", tags: (Array.isArray(c.tags) ? c.tags : []).join("; "),
      source: c.sourceDetail || SOURCE_LABELS[src] || src, created: c.customerSince || msToDay(c.createdAt),
      jobs: r.jobs, ltv: round2(r.won), notes: c.notes || "",
    };
  });
  return { columns, rows };
}

function estimateNo(q: any): string { return q.quoteNo ? "Q-" + q.quoteNo : "EST " + String(q.id || "").slice(-6).toUpperCase(); }
function quotesExport(s: any) {
  const columns = [
    { key: "no", label: "Estimate #" }, { key: "customer", label: "Customer" }, { key: "phone", label: "Phone" }, { key: "email", label: "Email" },
    { key: "address", label: "Address" }, { key: "product", label: "Product" }, { key: "lf", label: "Linear feet" }, { key: "status", label: "Status" },
    { key: "total", label: "Total" }, { key: "paid", label: "Paid" }, { key: "created", label: "Created" }, { key: "sent", label: "First sent" },
    { key: "lastSent", label: "Last sent" }, { key: "signed", label: "Signed" }, { key: "lost", label: "Lost reason" }, { key: "source", label: "Lead source" },
    { key: "rep", label: "Rep" }, { key: "archived", label: "Archived" },
  ];
  const rows = (s.quotes || []).filter(Boolean).map((q: any) => {
    const sends = Array.isArray(q.sends) ? q.sends : [];
    const firstSent = sends.length ? Number(sends[0].at) : Number(q.lastSentAt) || 0;
    const signedAt = (q.signature && q.signature.at ? Date.parse(q.signature.at) : 0) || Number(q.signedAt) || Number(q.wonAt) || 0;
    return {
      no: estimateNo(q), customer: q.customer || "", phone: q.phone || "", email: q.email || "", address: q.address || "",
      product: q.productName || "", lf: Number(q.lf) > 0 ? Math.round(Number(q.lf)) : "",
      status: (DOC_LABEL as any)[docState(q)] || q.status || "", total: round2(q.total), paid: round2(paidOf(q)),
      created: msToDay(q.createdAt), sent: msToDay(firstSent), lastSent: msToDay(q.lastSentAt || firstSent),
      signed: msToDay(signedAt), lost: q.status === "lost" ? q.lostReason || "" : "",
      source: SOURCE_LABELS[String(q.source || "").toLowerCase()] || q.source || "", rep: q.rep || "", archived: q.archivedAt ? msToDay(q.archivedAt) : "",
    };
  });
  return { columns, rows };
}

/* opts: { costs } — labor/material/hours columns only for roles that see cost */
function jobsExport(s: any, opts?: { costs?: boolean }) {
  const crews = ((s.settings && s.settings.crews) || []) as any[];
  const costs = !!(opts && opts.costs);
  const columns: any[] = [
    { key: "title", label: "Job" }, { key: "customer", label: "Customer" }, { key: "address", label: "Address" }, { key: "status", label: "Status" },
    { key: "start", label: "Install date" }, { key: "end", label: "End date" }, { key: "completed", label: "Completed date" },
    { key: "total", label: "Total" }, { key: "lf", label: "Linear feet" }, { key: "crew", label: "Crew" }, { key: "crewHours", label: "Crew hours (est.)" },
  ];
  if (costs) columns.push({ key: "labor", label: "Labor cost" }, { key: "material", label: "Material cost" }, { key: "hours", label: "Hours" });
  columns.push({ key: "from", label: "Came from" }, { key: "ref", label: "Job number" }, { key: "created", label: "Created" }, { key: "notes", label: "Notes" });
  const rows = (s.workOrders || []).filter(Boolean).map((w: any) => {
    const crew = crews.find((c: any) => c && c.id === w.crewId);
    const act = w.importedActuals || {};
    const logged = (Array.isArray(w.timeLogs) ? w.timeLogs : []).reduce((a: number, e: any) => a + (Number(e && e.hours) || 0), 0);
    const spent = (Array.isArray(w.expenses) ? w.expenses : []).filter((e: any) => !e.scope || e.scope === "job").reduce((a: number, e: any) => a + (Number(e && e.amount) || 0), 0);
    const r: any = {
      title: w.title || "", customer: w.customer || "", address: w.address || "", status: STAGE_LABEL[w.status] || w.status || "",
      start: w.scheduledStart || "", end: w.scheduledEnd || "", completed: msToDay(w.completedAt),
      total: round2(w.total), lf: Number(w.lf) > 0 ? Math.round(Number(w.lf)) : "", crew: crew ? crew.name || "" : "", crewHours: Number(w.crewHours) > 0 ? Number(w.crewHours) : "",
      from: w.imported ? "Import" : w.quoteId ? "Estimate" : "Added by hand", ref: w.sourceRef || "", created: msToDay(w.createdAt), notes: w.notes || "",
    };
    if (costs) {
      r.labor = act.laborCost != null ? round2(act.laborCost) : "";
      r.material = act.materialCost != null ? round2(act.materialCost) : spent > 0 ? round2(spent) : "";
      r.hours = act.hours != null ? act.hours : logged > 0 ? round2(logged) : "";
    }
    return r;
  });
  return { columns, rows };
}

/* opts: { perFtOf(product) } — the installed $/ft on the shared sample job */
function productsExport(products: any[], opts?: { perFtOf?: (p: any) => number }) {
  const columns = [
    { key: "name", label: "Name" }, { key: "category", label: "Material" }, { key: "sub", label: "Style" }, { key: "height", label: "Height" },
    { key: "section", label: "Material cost / section" }, { key: "width", label: "Section length (ft)" }, { key: "perFt", label: "Material cost / ft" },
    { key: "post", label: "Post cost" }, { key: "gate", label: "Gate cost" }, { key: "installed", label: "Installed price / ft (sample job)" },
    { key: "sku", label: "SKU" }, { key: "supplier", label: "Supplier" }, { key: "custom", label: "Yours or built-in" }, { key: "notes", label: "Notes" },
  ];
  const rows = (products || []).filter(Boolean).map((p: any) => {
    const w = Number(p.panelWidth) || 0;
    const flat = p._editor && p._editor.flatRate ? Number(p._editor.flatPerFt) : null;
    const perFt = flat != null && Number.isFinite(flat) ? flat : w > 0 ? (Number(p.panelCost) || 0) / w : "";
    let installed: any = "";
    try { if (opts && opts.perFtOf) { const n = Number(opts.perFtOf(p)); if (Number.isFinite(n) && n > 0) installed = round2(n); } } catch (e) { /* the engine couldn't price it */ }
    return {
      name: p.name || "", category: p.category || "", sub: p.sub || "", height: p.heightFt || (p._editor && p._editor.height) || "",
      section: round2(p.panelCost), width: w || "", perFt: perFt === "" ? "" : round2(perFt), post: round2(p.postKit), gate: Number(p.gateCost) > 0 ? round2(p.gateCost) : "",
      installed, sku: p.sku || "", supplier: p.supplier || "", custom: p.custom ? "Yours" : "Built-in", notes: p.notes || "",
    };
  });
  return { columns, rows };
}

export {
  IMPORT_MAX_BYTES, IMPORT_MAX_ROWS, PREVIEW_ROWS, HISTORY_KEEP, UNDO_SLICE, UNDO_SLICE_PRODUCTS, PRICEBOOK_MAX,
  CUSTOMER_FIELDS, JOB_FIELDS, PRODUCT_FIELDS, FENCE_CATS, SOURCE_LABELS, STAGE_LABEL, TEMPLATES,
  fieldsFor, hkey, autoMap, cellOf, normName, validEmail, emailsIn, phoneDigits, formatPhone, splitPhones, splitTags, mapSource, composeAddr, streetKey,
  parseNum, parseHeightFt, parseDay, dayToMs, msToDay, round2, mapJobStatus, mapCategory,
  customerIndex, findCustomerMatch, readCustomerRow, newCustomerRecord, customerFill, emptyPlan, tally, fieldWord,
  planCustomers, planJobs, readProductRow, planProducts, productSet, setWord, getPath, setPath, applySet, previewRows,
  batchFromPlan, applyImportBatch, importLeft, revertImportBatch, templateRows,
  customerRollup, customersExport, estimateNo, quotesExport, jobsExport, productsExport,
};
